import { isWorkCreditError } from '@/lib/workCredits';
import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View, Pressable, StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import Message from '@/components/Messages/Message';
import WorkBrowserArtifact from '@/components/Coworker/WorkBrowserArtifact';
import WorkAppear from '@/components/Coworker/WorkAppear';
import WorkRunHeader from '@/components/Coworker/WorkRunHeader';
import WorkStepSummary from '@/components/Coworker/WorkStepSummary';
import WorkActivityTrail from '@/components/Coworker/WorkActivityTrail';
import WorkProgressUpdate from '@/components/Coworker/WorkProgressUpdate';
import { useWorkLive } from '@/components/Coworker/WorkLiveContext';
import { useWorkNotesVisibility } from '@/components/Coworker/WorkNotesVisibility';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import {
  workMessageForDisplay,
  isWorkResumable,
  isWorkProgressMessage,
  isWorkCompletedFinalReply,
  shouldShowWorkProgressBody,
  workProgressFoldLabel,
} from '@/constants/work';

function MessageHighlightWrapperSlot({ Wrapper, messageIds, children }) {
  if (!Wrapper) return children;
  return <Wrapper messageIds={messageIds}>{children}</Wrapper>;
}

/**
 * Spinner + what the agent is doing right now. Always the list-end row
 * while the run is live — never a substitute for a finished comment.
 */
const WorkLiveStatusLine = React.memo(function WorkLiveStatusLine({ live, appear = false, style }) {
  const liveState = useWorkLive();
  // Task status and transcript rows can settle on different renders. Missing
  // activity is not evidence that a new computer is starting; real startup
  // events already supply the boot message through activityStatus.
  const phrase = liveState?.activityStatus || live?.summary?.text || 'Updating work status';
  const line = (
    <WorkStepSummary
      icon={liveState?.activityIcon}
      text={phrase}
      isLive
      stepStartTs={liveState?.activityStartedAt ?? null}
      liveStartTs={live?.startTs ?? liveState?.liveStartTs ?? null}
    />
  );
  if (!appear) {
    return style ? <View style={style}>{line}</View> : line;
  }
  return (
    <WorkAppear style={style} duration={220} distance={6}>
      {line}
    </WorkAppear>
  );
});

WorkLiveStatusLine.propTypes = {
  live: PropTypes.object,
  appear: PropTypes.bool,
  style: PropTypes.object,
};

/**
 * List-end status for a live run. This is the guaranteed working indicator
 * — it stays up after comments land and while the next tool is in flight.
 */
export const WorkLiveActivityRow = React.memo(function WorkLiveActivityRow({ style, live }) {
  return <View style={style}>
    <WorkLiveStatusLine live={live} appear />
  </View>;
});

WorkLiveActivityRow.propTypes = {
  style: PropTypes.object,
  live: PropTypes.object,
};

/**
 * A user request in a Work thread: the bubble, then the steps the agent took
 * right after it (including after a steer), logged in the order they
 * happened.
 */
export const WorkUserTranscriptRow = React.memo(function WorkUserTranscriptRow({
  children,
  workRun,
  workColumnStyle,
  taskStatus,
}) {
  const liveState = useWorkLive();
  const runStatus = workRun?.isLatestRun ? taskStatus : workRun?.terminalStatus;
  return (
    <View>
      {children}
      {!!workRun?.browserArtifact && <View style={workColumnStyle}><WorkBrowserArtifact artifact={workRun.browserArtifact} /></View>}
      {!!workRun?.activityLines?.length && (
        <View style={workColumnStyle}>
          <WorkActivityTrail
            lines={workRun.activityLines}
            isLive={!!workRun.isLiveTrail}
            liveText={workRun.isLiveTrail ? (liveState?.activityStatus || '') : ''}
          />
        </View>
      )}
      {!!workRun?.settledWithoutReply && (
        <View style={workColumnStyle}>
          {!isWorkResumable(runStatus) && (
            <WorkStepSummary
              icon={workRun.waitingOnUser ? 'circle-question' : workRun.emptyOutcome ? 'circle-exclamation' : 'circle-check'}
              text={workRun.waitingOnUser ? 'Paused for your answer' : workRun.emptyOutcome
                ? 'Finished without a visible result'
                : 'Ready for review'}
              isLive={false}
            />
          )}
          <WorkRunHeader
            durationMs={workRun.durationMs}
            status={runStatus}
            cancellationPending={!!workRun.isLatestRun && liveState?.cancellationPending}
            creditPaused={!!workRun.isLatestRun && isWorkCreditError(liveState?.taskError)}
            attribution={workRun.attribution?.text}
          />
        </View>
      )}
    </View>
  );
});

WorkUserTranscriptRow.propTypes = {
  children: PropTypes.node,
  workRun: PropTypes.object,
  workColumnStyle: PropTypes.object,
  taskStatus: PropTypes.string,
};

function WorkProgressNotesFold({ fold, isOpen, onToggle }) {
  const { colors } = useTheme();
  const count = fold?.hiddenCount || 0;
  if (count < 1) return null;
  const summaryLabel = workProgressFoldLabel(fold);
  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="button"
      accessibilityLabel={`${summaryLabel}. ${isOpen ? 'Hide' : 'Show'} the working notes`}
      style={({ hovered, pressed }) => [
        foldStyles.row,
        (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
      ]}
    >
      <Icon
        name={isOpen ? 'chevron-down' : 'chevron-right'}
        size={12}
        color={colors.text_color_tertiary}
        style={foldStyles.icon}
      />
      <Text size="n" color="secondary" numberOfLines={1} style={foldStyles.text}>
        {summaryLabel}
      </Text>
    </Pressable>
  );
}

WorkProgressNotesFold.propTypes = {
  fold: PropTypes.object,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
};

const foldStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    paddingVertical: 3,
    paddingHorizontal: 6,
    marginLeft: -6,
    marginBottom: 4,
    borderRadius: 6,
  },
  icon: {
    marginTop: 4,
    width: 18,
    textAlign: 'center',
  },
  text: {
    flex: 1,
  },
});

export const WorkTranscriptMessage = React.memo(function WorkTranscriptMessage({
  item,
  index,
  lastIndex,
  conversationId,
  isOwner,
  chatTitle,
  workRun,
  workColumnStyle,
  taskStatus,
  HighlightWrapper,
  onBodyLayout,
}) {
  // Stable identity across parent re-renders — a fresh object every render
  // defeated the memo on Message/WorkProgressUpdate below, so every live
  // activity tick re-rendered the reply's whole markdown tree.
  const paused = !!workRun?.isLatestRun
    && !!workRun?.isFinalReply
    && taskStatus !== 'stopped'
    && isWorkResumable(taskStatus);
  const workActions = isWorkCompletedFinalReply(workRun) ? 'final' : 'chrome';
  const displayItem = useMemo(() => ({
    ...item,
    content: workMessageForDisplay(item.content, { paused }),
    thinkingContent: '',
    isThinking: false,
    workActions,
  }), [item, paused, workActions]);
  const isLiveProgressUpdate = isWorkProgressMessage(displayItem, workRun);
  const activityLines = workRun?.activityLines;
  const hasActivityTrail = !!activityLines?.length;
  const liveState = useWorkLive();
  const { isFoldOpen, toggleFold } = useWorkNotesVisibility();
  const progressFold = workRun?.progressFold;
  const foldOpen = !!(progressFold?.key && isFoldOpen(progressFold.key));
  const showProgressBody = shouldShowWorkProgressBody(workRun, { foldOpen });
  const hideCreditError = !!workRun?.isLatestRun && isWorkResumable(taskStatus) && isWorkCreditError(item.content) && isWorkCreditError(liveState?.taskError);
  const hideWorkBody = hideCreditError || (!String(displayItem.content || '').trim()
    && !(item.files?.length
      || item.images?.length
      || item.parameters?.coworkerArtifacts?.length))
    || !showProgressBody;
  const isFoldLead = progressFold?.role === 'lead';
  const stepText = workRun?.isLiveStep
    ? null
    : workRun?.stepSummary?.text;

  const handleBodyLayout = useCallback((event) => {
    onBodyLayout?.(item.id, event.nativeEvent.layout.height);
  }, [item.id, onBodyLayout]);
  useLayoutEffect(() => {
    if (hideWorkBody) onBodyLayout?.(item.id, 0);
  }, [hideWorkBody, item.id, onBodyLayout]);

  if (
    hideWorkBody
    && !isFoldLead
    && !workRun?.isRunEnd
    && !hasActivityTrail
    && !workRun?.browserArtifact
    && !workRun?.isLiveStep
    && !stepText
  ) {
    return null;
  }

  return (
    <MessageHighlightWrapperSlot Wrapper={HighlightWrapper} messageIds={[item.id]}>
      <View style={{ overflowAnchor: 'none' }}>
        {isFoldLead ? (
          <View style={workColumnStyle}>
            <WorkProgressNotesFold
              fold={progressFold}
              isOpen={foldOpen}
              onToggle={() => toggleFold(progressFold.key)}
            />
          </View>
        ) : null}
        {hideWorkBody ? null : (
          // A live reply's row mounts on its first token (empty drafts are
          // hidden), which used to pop a bubble in fully formed. Settled
          // rows mount statically so scrolling history never shimmers.
          <WorkAppear enabled={!!workRun?.isLiveStep} duration={200} onLayout={handleBodyLayout}>
            {isLiveProgressUpdate ? (
              <View style={workColumnStyle}>
                <WorkProgressUpdate conversationId={conversationId} message={displayItem} />
              </View>
            ) : (
              <Message
                conversationId={conversationId}
                isOwner={isOwner}
                chatTitle={chatTitle}
                message={displayItem}
                isLastMessage={index === lastIndex}
              />
            )}
          </WorkAppear>
        )}
        {!!workRun?.browserArtifact && <View style={workColumnStyle}><WorkBrowserArtifact artifact={workRun.browserArtifact} /></View>}
        {hasActivityTrail ? (
          <View style={workColumnStyle}>
            <WorkActivityTrail
              lines={activityLines}
              isLive={!!workRun?.isLiveTrail}
              liveText={workRun?.isLiveTrail ? (liveState?.activityStatus || '') : ''}
            />
          </View>
        ) : (
          !!stepText && (
            <View style={workColumnStyle}>
              <WorkStepSummary
                icon={workRun.stepSummary?.icon}
                text={stepText}
                isLive={false}
              />
            </View>
          )
        )}
        {!!workRun?.isRunEnd && (
          // The run's receipt — time + attribution — after the answer and its
          // deliverables, closing the run the way it reads in a log.
          <View style={workColumnStyle}>
            <WorkRunHeader
              durationMs={workRun.durationMs}
              status={workRun.terminalStatus || (workRun.isLatestRun ? taskStatus : null)}
              cancellationPending={!!workRun.isLatestRun && liveState?.cancellationPending}
              creditPaused={!!workRun.isLatestRun && isWorkCreditError(liveState?.taskError)}
              attribution={workRun.attribution?.text}
            />
          </View>
        )}
      </View>
    </MessageHighlightWrapperSlot>
  );
});

WorkTranscriptMessage.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  lastIndex: PropTypes.number.isRequired,
  conversationId: PropTypes.string,
  isOwner: PropTypes.bool,
  chatTitle: PropTypes.string,
  workRun: PropTypes.object,
  workColumnStyle: PropTypes.object,
  taskStatus: PropTypes.string,
  HighlightWrapper: PropTypes.elementType,
  onBodyLayout: PropTypes.func,
};
